import { useEffect, useRef } from 'react';
import { useLocation } from 'wouter';

interface SwipeBackOptions {
  /** Disable swipe back globally */
  disabled?: boolean;
  /** Edge distance in pixels to trigger swipe */
  edgeThreshold?: number;
  /** Minimum swipe distance to trigger navigation */
  swipeThreshold?: number;
}

/**
 * Custom hook for iOS-style swipe-back navigation
 * Allows users to swipe from the left edge to go back to the previous page
 */
export function useSwipeBack(options: SwipeBackOptions = {}) {
  const {
    disabled = false,
    edgeThreshold = 50, // Only detect swipes starting within 50px of left edge
    swipeThreshold = 100 // Need to swipe at least 100px to trigger navigation
  } = options;

  const [location, navigate] = useLocation();
  const touchStartX = useRef<number>(0);
  const touchStartY = useRef<number>(0);
  const isSwiping = useRef<boolean>(false);

  useEffect(() => {
    if (disabled) return;

    const handleTouchStart = (e: TouchEvent) => {
      // Check if we're starting within the left edge threshold
      const touch = e.touches[0];
      if (touch.clientX <= edgeThreshold) {
        touchStartX.current = touch.clientX;
        touchStartY.current = touch.clientY;
        isSwiping.current = true;
      }
    };

    const handleTouchMove = (e: TouchEvent) => {
      if (!isSwiping.current) return;

      const touch = e.touches[0];
      const deltaX = touch.clientX - touchStartX.current;
      const deltaY = touch.clientY - touchStartY.current;

      // Cancel swipe if moving more vertically than horizontally
      if (Math.abs(deltaY) > Math.abs(deltaX)) {
        isSwiping.current = false;
      }
    };

    const handleTouchEnd = (e: TouchEvent) => {
      if (!isSwiping.current) return;

      const touch = e.changedTouches[0];
      const deltaX = touch.clientX - touchStartX.current;
      const deltaY = touch.clientY - touchStartY.current;

      // Check if this is a valid horizontal swipe to the right
      if (
        deltaX > swipeThreshold && // Swiped far enough to the right
        Math.abs(deltaY) < Math.abs(deltaX) // More horizontal than vertical
      ) {
        // Navigate back
        window.history.back();
      }

      // Reset state
      isSwiping.current = false;
      touchStartX.current = 0;
      touchStartY.current = 0;
    };

    // Add event listeners
    document.addEventListener('touchstart', handleTouchStart, { passive: true });
    document.addEventListener('touchmove', handleTouchMove, { passive: true });
    document.addEventListener('touchend', handleTouchEnd, { passive: true });

    return () => {
      document.removeEventListener('touchstart', handleTouchStart);
      document.removeEventListener('touchmove', handleTouchMove);
      document.removeEventListener('touchend', handleTouchEnd);
    };
  }, [disabled, edgeThreshold, swipeThreshold, navigate]);

  return {
    /** Current location */
    location
  };
}
